import { useMemo } from 'react';
import { useCourse } from './useCourse';
import type { CourseIdType } from '@/domain/precon-supporter/value-objects/CourseId';
import type { LectureData } from '@/domain/precon-supporter/types/course';

/**
 * 講座データとナビゲーション情報を提供するカスタムフック
 *
 * @param courseId - コースID
 * @param lectureId - 現在の講座ID
 * @returns 講座データ、ナビゲーション情報、読み込み状態
 */
export const useLectureData = (courseId: CourseIdType, lectureId: string) => {
  const { course, loading: courseLoading } = useCourse(courseId);

  // Required講座のみの配列（ナビゲーション用）
  const requiredLectures = course?.requiredLectures || [];

  // 全講座の配列（配列のマージは計算コストがあるためメモ化）
  const allLectures = useMemo(
    () => [
      ...(course?.requiredLectures || []),
      ...(course?.optionalLectures || []),
    ],
    [course]
  );

  // 現在の講座
  const currentLecture = useMemo(
    () => allLectures.find(l => l.id === lectureId),
    [allLectures, lectureId]
  );

  // ナビゲーション情報（Required講座のみを対象）
  const navigation = useMemo(() => {
    const currentIndexInRequired = requiredLectures.findIndex(
      l => l.id === lectureId
    );

    const previousLecture: LectureData | null =
      currentIndexInRequired > 0
        ? requiredLectures[currentIndexInRequired - 1]
        : null;

    const nextLecture: LectureData | null =
      currentIndexInRequired >= 0 &&
      currentIndexInRequired < requiredLectures.length - 1
        ? requiredLectures[currentIndexInRequired + 1]
        : null;

    return {
      currentIndexInRequired,
      previousLecture,
      nextLecture,
    };
  }, [requiredLectures, lectureId]);

  // 完了済み講座IDリスト生成用のヘルパー関数
  const getCompletedLectureIds = (
    isLectureCompletedFn: (id: string) => boolean
  ) => {
    return allLectures
      .filter(lecture => isLectureCompletedFn(lecture.id))
      .map(lecture => lecture.id);
  };

  return {
    course,
    courseLoading,
    allLectures,
    requiredLectures,
    currentLecture,
    navigation,
    getCompletedLectureIds,
  };
};
